import { CacheClientService } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { DataGrid, RowItem } from "../../../components/data-grid/data-grid";
import { transient, autoinject } from "aurelia-dependency-injection";
import { bindable } from "aurelia-framework";
import { TableMetaData } from "../../../../models/lookups/table-metadata";
import { DataType, DataGridColumn, ItemSource, SourceType } from "../../../../models/queries/queries.models";
import { TableColumnMetaData } from "../../../../models/lookups/table-column-metadata";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { BindingEngine } from "aurelia-binding";
import { I18N } from 'aurelia-i18n';
import {
    LookupValueRequest,
    PersistLookupRequest,
    PersistLookupValuesRequest,
    UpdateLookupValuesResponse,
    LookupValue
} from "../../../../models/lookups/lookup.models";
import { StringUtilities } from "../../../../utilities/strings";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { computedFrom } from "aurelia-binding";
import { Reflection } from "../../../../utilities/reflection";
import { CacheCategory } from "../../../../constants/lookups/lookup.constants";
import { Guid } from "../../../../utilities/guid";
import { BaseClaims } from "../../../../constants/authentication/base-claims";
import "moment";
import * as moment from "moment";
import { Router } from "aurelia-router";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";

@autoinject
@transient()
export class ManageSelectedLookup {

    private originalSequenceNumbering: { name: string, sequenceValue: number }[];
    private _alreadySortedAlphabetically = false;
    public isBranchable = false;
    public _updateLookupsGlobally;

    @bindable()
    public tableMetaData: TableMetaData = null;

    public tableColumnMetaDataItems: TableColumnMetaData[];
    public lookupValues: LookupValue[] = [];
    public levels: any[] = [];
    public lookupValuesUow: UnitOfWorkObserver;
    public columns: DataGridColumn[] = [];
    public lookups: ItemSource;
    public dataGridVM: DataGrid;
    public canAddNewValue: boolean = true;
    public canDeleteValues: boolean = true;
    public canEditValues: boolean = true;
    public selectedLookupValue: LookupValue = null;
    public canUpdateLookupsGlobally: boolean;

    @computedFrom(Reflection.getPropertyPath<ManageSelectedLookup>(x => x.tableMetaData), Reflection.getPropertyPath<ManageSelectedLookup>(x => x.tableMetaData.sortAlphabetical))
    private get isMovable(): boolean {
        return this.tableMetaData && !this.tableMetaData.sortAlphabetical;
    }

    private get maxSequenceNumber(): number {
        return this.lookupValues && this.lookupValues.length > 0
            ? Enumerable.from(this.lookupValues.map(meta => meta.sequenceNumber ? meta.sequenceNumber : 0)).max()
            : 0;
    }

    @computedFrom(Reflection.getPropertyPath<ManageSelectedLookup>(x => x.tableMetaData.sortAlphabetical), Reflection.getPropertyPath<ManageSelectedLookup>(x => x.selectedLookupValue.sequenceNumber))
    get isMovableUp(): boolean {
        return this.isMovable
            && this.selectedLookupValue
            && this.selectedLookupValue.sequenceNumber > 0;
    }

    @computedFrom(Reflection.getPropertyPath<ManageSelectedLookup>(x => x.tableMetaData.sortAlphabetical), Reflection.getPropertyPath<ManageSelectedLookup>(x => x.selectedLookupValue.sequenceNumber))
    get isMovableDown(): boolean {
        return this.isMovable
            && this.selectedLookupValue
            && this.selectedLookupValue.sequenceNumber < this.maxSequenceNumber;
    }

    @computedFrom(
        Reflection.getPropertyPath<ManageSelectedLookup>(x => x.canUpdateLookupsGlobally),
        Reflection.getPropertyPath<ManageSelectedLookup>(x => x.isBranchable),
        Reflection.getPropertyPath<ManageSelectedLookup>(x => x._updateLookupsGlobally),
    )
    get updateLookupsGlobally(): boolean {
        if (!this.canUpdateLookupsGlobally) return false;

        if (!this.isBranchable) return true;

        if (this._updateLookupsGlobally == null) {
            this._updateLookupsGlobally = true;
        }

        return this._updateLookupsGlobally;
    }

    set updateLookupsGlobally(value: boolean) {
        this._updateLookupsGlobally = value;
    }

    constructor(private lookupClientService: LookupClientService,
        private engine: BindingEngine,
        private cachingService: CacheClientService,
        private userClaimsService: UserClaimsService,
        private notificationClient: NotificationClientService,
        private i18N: I18N, 
        private dialogClientService:DialogClientService,
        private router:Router, private configService: ConfigService) {
    }

    onSortAlphabeticalChanged() {
        if (this.tableMetaData.sortAlphabetical) {
            if (this._alreadySortedAlphabetically)
                return;

            this._alreadySortedAlphabetically = true;
            this.originalSequenceNumbering = [];
            Enumerable.from(this.lookupValues).orderBy(x => x.name.toLowerCase()).forEach((lookupValue, index) => {
                const sequenceNumber = CloneHelper.clone(lookupValue.sequenceNumber ? lookupValue.sequenceNumber : 0);
                this.originalSequenceNumbering.push({ name: lookupValue.name, sequenceValue: sequenceNumber });
                lookupValue.sequenceNumber = index;
            });
            this.lookupValues = Enumerable.from(this.lookupValues).orderBy(x => x.name).toArray();
        }
        else {
            Enumerable.from(this.lookupValues).orderBy(x => x.sequenceNumber).forEach((lookupValue, index) => {
                const originalLookupValueItem = Enumerable.from(this.originalSequenceNumbering).firstOrDefault(x => x.name == lookupValue.name);
                if (originalLookupValueItem)
                    lookupValue.sequenceNumber = originalLookupValueItem.sequenceValue ? originalLookupValueItem.sequenceValue : 0;
                else
                    lookupValue.sequenceNumber = index
            });
            this._alreadySortedAlphabetically = false;
        }
        this.dataGridVM.reloadData();
    }

    onSelectedLookupValue($event: { detail: { item: any, row: RowItem, identifier: string } }) {
        this.selectedLookupValue = $event && $event.detail ? $event.detail.item : null;
    }

    tableMetaDataChanged() {
        if (!this.tableMetaData) {
            return;
        }
        this.canAddNewValue = this.userClaimsService.hasPermission(BaseClaims.CanAddLookups) &&
            (!this.tableMetaData.addPermission || this.userClaimsService.hasPermission(this.tableMetaData.addPermission.name));

        this.canDeleteValues = this.userClaimsService.hasPermission(BaseClaims.CanDeleteLookups) &&
            (!this.tableMetaData.deletePermission || this.userClaimsService.hasPermission(this.tableMetaData.deletePermission.name));

        this.canEditValues = this.userClaimsService.hasPermission(BaseClaims.CanUpdateLookups) &&
            (!this.tableMetaData.updatePermission || this.userClaimsService.hasPermission(this.tableMetaData.updatePermission.name));

        this.canUpdateLookupsGlobally = this.userClaimsService.hasPermission(BaseClaims.UpdateLookupsGlobally);
        this.lookupValues = [];
        this.columns = [];
        this.loadLookupData();
    }

    deleteLookup(item: any) {
        const index = this.lookupValues.indexOf(item);
        this.lookupValues.splice(index, 1);
    }

    add() {
        const x: any = {};
        for (const col of this.columns) {
            x[col.name] = null;
        }

        x.parentId = this.levels && this.levels.length > 0 ? this.levels[this.levels.length - 1].id : null;
        x.isEditable = true;
        //we keep the new entry on top until the user hits save for UX purposes
        x.sequenceNumber = -1;
        x.guid = Guid.generateGuid();
        x.isActive = true;
        x.isDefault = false;

        this.lookupValues.push(x);
    }

    moveUp(): void {
        if (!(this.selectedLookupValue && this.selectedLookupValue.sequenceNumber > 0)) {
            return;
        }

        this.lookupValues.filter(meta => meta.sequenceNumber == this.selectedLookupValue.sequenceNumber - 1).forEach(meta => {
            meta.sequenceNumber++;
        })

        this.selectedLookupValue.sequenceNumber--;
        this.dataGridVM.reloadData();
    }

    moveDown(): void {
        if (!(this.selectedLookupValue && this.selectedLookupValue.sequenceNumber < this.maxSequenceNumber)) {
            return;
        }

        this.lookupValues.filter(meta => meta.sequenceNumber == this.selectedLookupValue.sequenceNumber + 1).forEach(meta => {
            meta.sequenceNumber--;
        })

        this.selectedLookupValue.sequenceNumber++;
        this.dataGridVM.reloadData();
    }

    private allData: any[] = [];

    loadLookupData() {
        return this.lookupClientService.getLookupColumns(this.tableMetaData.lookupId).then((response) => {
            this.levels.splice(0, this.levels.length);
            this.allData = CloneHelper.clone(response.tableRowData);
            this._alreadySortedAlphabetically = false;
            this.isBranchable = response.isBranchable;

            this.setValues(response.tableColumnMetadatas, response.tableRowData);
        }).catch((e) => {
            console.error(e);
        });
    }

    getSecondParameter(tableColumnMetaData: TableColumnMetaData) {
        if (tableColumnMetaData.tableColumnDataTypeId == DataType.Date) {
            return this.configService.config.common.defaultDateFormat;
        } else if (tableColumnMetaData.tableColumnDataTypeId == DataType.Time) {
            return  this.configService.config.common.defaultTimeFormat;
        } else if (tableColumnMetaData.tableColumnDataTypeId == DataType.DateTime) {
            return this.configService.config.common.defaultDateFormat;
        }
        return null;
    }

    setValues(tableColumnMetadata: TableColumnMetaData[], tableRowData: any[]) {
        //ordering of columns by sequence number in db of TableColumnMetaData
        this.tableColumnMetaDataItems = Enumerable.from(tableColumnMetadata).orderBy(x => <number>x.sequenceNumber).toArray();

        const array: DataGridColumn[] = [];

        array.push({
            name: "id",
            isSortable: true,
            sortOrder: array.length,
            columnOrder: array.length,
            dataType: DataType.Number,
            isEditable: false,
            readOnly: true,
            title: "#",
            valueConverter: (val, item: any) => item.id
        });

        for (const tableColumnItem of this.tableColumnMetaDataItems) {
            if (tableColumnItem.isPrimaryKeyColumn || tableColumnItem.columnName.toLowerCase() == "parentid") {
                continue;
            }

            const columnName = StringUtilities.camelize(tableColumnItem.columnName);
            const column: DataGridColumn = {
                name: columnName,
                sortOrder: array.length,
                columnOrder: array.length,
                parameter: tableColumnItem.relatedLookupName,
                secondParameter: this.getSecondParameter(tableColumnItem),
                thirdParameter: tableColumnItem.tableColumnDataTypeId == DataType.Double && tableColumnItem.precision ? tableColumnItem.precision : undefined,
                dataType: tableColumnItem.relatedLookupId ? (tableColumnItem.isMultiDimensionalRelation ? DataType.HierarchicalLookup : DataType.Lookup) : tableColumnItem.tableColumnDataTypeId,
                title: tableColumnItem.columnDisplayName,
                isFilterable: true,
                readOnly: tableColumnItem.updatePermissionId ? !this.userClaimsService.hasPermissionById(tableColumnItem.updatePermissionId) : false
            };

            if (column.dataType == DataType.DateTime) {
                column.valueConverter = x => moment(new Date(x)).format(this.configService.config.common.defaultDateTimeFormat);
            } else if (column.dataType == DataType.Date) {
                column.valueConverter = x => moment(new Date(x)).format(this.configService.config.common.defaultDateFormat);
            } else if (column.dataType == DataType.Time) {
                column.valueConverter = x => moment(new Date(x)).format(this.configService.config.common.defaultTimeFormat);
            }

            array.push(column);
        }

        if (this.canEditValues) {
            array.push({
                name: "edit",
                isSortable: false,
                instancePerRow: true,
                sortOrder: array.length,
                columnOrder: array.length,
                dataType: DataType.Icon,
                title: this.i18N.tr('edit-text'),
                parameter: "edit",
                value: (x, row: RowItem) => {
                    x.isEditable = !x.isEditable;
                    row.columnKeys["edit"].column.parameter = row.columnKeys["edit"].column.parameter === "edit" ? "done" : "edit";
                    row.refresh();
                }
            });
        }

        if (this.canDeleteValues) {
            array.push({
                name: "delete",
                isSortable: false,
                sortOrder: array.length,
                columnOrder: array.length,
                dataType: DataType.Icon,
                title: this.i18N.tr('delete-text'),
                parameter: "close",
                value: x => {
                    this.deleteLookup(x);
                }
            });
        }

        if (this.tableMetaData.hierarchicalMaxNumberOfLevels > 1 &&
            this.tableMetaData.hierarchicalMaxNumberOfLevels > this.levels.length + 1) {
            array.push({
                name: "",
                sortOrder: array.length,
                columnOrder: array.length,
                dataType: DataType.Icon,
                title: "",
                parameter: "navigate_next",
                value: x => {
                    this.levels.push(x);
                    this.lookupValues = [];
                    this.columns = [];
                    setTimeout(() => { this.setValues(this.tableColumnMetaDataItems, x.children); });
                }
            });
        }


        this.columns = array;
        this.originalSequenceNumbering = []
        tableRowData.forEach((x, index) => {
            //set original sequence number
            const sequenceNumber = CloneHelper.clone(x.sequenceNumber ? x.sequenceNumber : index);
            this.originalSequenceNumbering.push({ name: x.name, sequenceValue: sequenceNumber });

            //set current sequence number according to current order in list
            x.sequenceNumber = index;
        });
        this.lookupValues = tableRowData;
        const sortingColumn = {
            name: "sequenceNumber",
            IsHidden: true,
            sortOrder: 0,
            columnOrder: array.length,
            dataType: DataType.Number,
            title: "",
            isAscending: true,
            isSortable: true
        }
        this.lookups = { source: this.lookupValues, sourceType: SourceType.Collection, columns: this.columns, initialSorting: [sortingColumn] };
        // these need to be async
        setTimeout(() => {
            if (this.lookupValuesUow) {
                this.lookupValuesUow.dispose();
            }
            this.lookupValuesUow = new UnitOfWorkObserver(this.engine, { lookupValues: this.lookupValues }, ["isEditable"]);
            this.lookupValuesUow.startTracking();
        });
    }

    goBack(x: any) {
        this.goBackInternal(x, CloneHelper.clone(this.allData));
        const index = this.levels.indexOf(x);
        this.levels.splice(index, this.levels.length - index);

        this.dataGridVM.reloadData("reload-from-lookups");
    }

    goBackInternal(x: any, values: any[]) {
        for (const value of values) {
            if (value.id == x.id) {
                this.lookupValues = [];
                this.columns = [];
                setTimeout(() => { this.setValues(this.tableColumnMetaDataItems, values); });
            } else if (value.children && value.children.length > 0) {
                this.goBackInternal(x, value.children);
            }
        }
    }

    openAuditsDialog(){
        const lookupAuditDialogOverrideUrl = this.router.currentInstruction.config.settings.lookupAuditDialogOverrideUrl;
        const url = lookupAuditDialogOverrideUrl != null?lookupAuditDialogOverrideUrl : DialogServiceConstants.URLs.LookupAuditDialog;
        this.dialogClientService.show(url, {entityType: EntityTypes.Lookup, action: this.tableMetaData.tableName}, true, false, false, "right");
    }

    save() {
        return new Promise<void>((resolve, reject) => {

            let maxSequenceNumber = this.maxSequenceNumber;

            //We get all the new lookups and set the approprioate sequence number to be them at 
            //the end after save
            this.lookupValues.filter(x => x.sequenceNumber < 0).forEach(lookup => {
                maxSequenceNumber++;
                lookup.sequenceNumber = maxSequenceNumber;
            });

            //We call set timeout to be able to trigger uow
            setTimeout(() => {

                const nameColumn = Enumerable.from(this.tableColumnMetaDataItems).firstOrDefault(x => x.isNameColumn);
                if (nameColumn && Enumerable.from(this.lookupValues).any(x => !x[StringUtilities.camelize(nameColumn.columnName)])) {
                    this.notificationClient.show(this.i18N.tr('validation-error-text'), nameColumn.columnDisplayName + " " + this.i18N.tr('is-required-text') + ".", NotificationType.Error)
                    reject();
                    return;
                }

                const request: PersistLookupValuesRequest = {
                    lookupId: this.tableMetaData.lookupId,
                    alphabeticalSorting: this.tableMetaData.sortAlphabetical,
                    persistLookupsRequest: <PersistLookupRequest>{ changes: this.lookupValuesUow.getChanges() },
                    updateLookupsGlobally: this.updateLookupsGlobally
                };
                this.lookupClientService.updateLookupValues(request).then(() => {
                    this.notificationClient.show(this.i18N.tr('successfully-saved-text') + "!", this.i18N.tr('lookup-has-been-successfully-saved-text'), NotificationType.Success);
                    this.cachingService.getKeysByCacheCategory<any>(CacheCategory.lookups).then(lookups => {
                        for (const data of lookups) {
                            if (data.request && this.tableMetaData.lookup && this.tableMetaData.lookup.name && data.request.lookupValueQuery
                                && data.request.lookupValueQuery.toLowerCase() == this.tableMetaData.lookup.name.toLowerCase()) {
                                const req = new LookupValueRequest();
                                req.lookupValueQuery = data.request.lookupValueQuery;
                                req.parameters = data.request.parameters;
                                this.cachingService.remove(req.getCacheKey());
                                console.log("clearing cache for lookup >> " + req.lookupValueQuery);
                            }
                        }
                    });
                    if (this.lookupValuesUow)
                        this.lookupValuesUow.clearChanges();
                    this.loadLookupData().then(() => {
                        resolve();
                    }).catch(() => { reject(); });
                }).catch((response: UpdateLookupValuesResponse) => {

                    if (response && response.exceptionNumber == 547) {
                        this.notificationClient.show(this.i18N.tr('warning') + "!", this.i18N.tr('lookup-cannot-be-deleted-in-use-text') + ".", NotificationType.Warning);
                    } else {
                        this.notificationClient.show(this.i18N.tr('error-text') + "!", this.i18N.tr('unknown-error-text') + ".", NotificationType.Error);
                    }

                    reject();
                });
            });
        });
    }
}
